import type {
  Activity,
  JourneyData,
  ContentVersion,
  WddChallenge,
  WddMultipleChoiceChallenge
} from "#shared/utils/types"
import type { CompassHistoryItem, GameHistoryItem } from "./types"
import type { WddPollChallenge } from "#shared/utils/types"

export const isPollChallenge = (
  challenge: WddChallenge | undefined
): challenge is WddPollChallenge => challenge?.fragment.kind === "wdd_poll"

export const scrollGame = () => {
  window.setTimeout(() => {
    const gameContainer = document.querySelector("#gameContainer")
    if (gameContainer) {
      gameContainer.scrollTo({
        top: gameContainer.scrollHeight,
        behavior: "smooth"
      })
    }
  }, 100)
}

export const sortActivities = (activities: Activity[]): Activity[] => {
  return [...activities].sort((a, b) => {
    if (a.created_at < b.created_at) return -1
    if (a.created_at > b.created_at) return 1
    return 0
  })
}

export const filterActivitiesByKind = (
  kind: string,
  activities: Activity[]
): Activity[] => {
  return activities.filter((a) => a.kind === kind)
}

export const retrieveOrderedChallengesForPath = (
  pathId: number | undefined | null,
  contentVersion: ContentVersion
) => {
  if (!pathId) return []

  const quest = contentVersion.quest
  if (!quest!.paths) return []
  const wddChallenges = quest!.challenges as WddChallenge[]
  return quest!.paths
    .find((p: Path) => p.id === pathId)
    ?.challenges.map((cid: number) => {
      return wddChallenges.find((c: WddChallenge) => c.id === cid)
    })
    .filter((c: WddChallenge | undefined): c is WddChallenge => c !== undefined) // Removes undefined values from the array
}

export const findCurrentChallenge = (
  journeyData: JourneyData,
  contentVersion: ContentVersion
): {
  challenge: WddChallenge | undefined
  index: number | undefined
} => {
  const activities = journeyData.group.activities
  const challenges = retrieveOrderedChallengesForPath(
    journeyData.group.path_id,
    contentVersion
  )

  const normalChallenges = challenges?.filter(
    (c: WddChallenge) => !c.name.startsWith("wdd_compass_")
  )

  const sortedActivities = sortActivities(activities)
  const solveChallengeActivities = filterActivitiesByKind(
    "solve_challenge",
    sortedActivities
  )

  if (normalChallenges) {
    // we have to find the last challenge that was solved, and then find the next challenge
    const lastSolveChallengeActivity = solveChallengeActivities.pop()

    const lastSolvedChallenge = normalChallenges.find(
      (c: WddChallenge) =>
        c.id === lastSolveChallengeActivity?.data.challenge_id
    )

    if (lastSolvedChallenge) {
      const currentChallengeIndex =
        normalChallenges.indexOf(lastSolvedChallenge)
      // if there is another challenge we return it
      if (normalChallenges[currentChallengeIndex + 1]) {
        const challenge = normalChallenges[currentChallengeIndex + 1]
        return {
          challenge: challenge,
          index: currentChallengeIndex + 1
        }
      }
      // if there is no other challenge, we return undefined (end of the game)
      return {
        challenge: undefined,
        index: undefined
      }
    } else {
      // if we didn't find the last challenge that was solved, we return the first challenge (start of the game)
      const challenge = normalChallenges[0]
      return {
        challenge: challenge,
        index: 0
      }
    }
  }
  return { challenge: undefined, index: undefined }
}

export const findCurrentCompassChallenge = (
  journeyData: JourneyData,
  contentVersion: ContentVersion
): {
  challenge: WddMultipleChoiceChallenge | undefined
  index: number | undefined
} => {
  const activities = journeyData.group.activities
  const challenges = retrieveOrderedChallengesForPath(
    journeyData.group.path_id,
    contentVersion
  )

  const compassChallenges = challenges?.filter((c: WddChallenge) =>
    c.name.startsWith("wdd_compass_")
  )

  const sortedActivities = sortActivities(activities)
  const solveChallengeActivities = filterActivitiesByKind(
    "solve_compass_challenge",
    sortedActivities
  )

  if (compassChallenges) {
    // we have to find the last challenge that was solved, and then find the next challenge
    const lastSolveChallengeActivity = solveChallengeActivities.pop()

    const lastSolvedChallenge = compassChallenges.find(
      (c: WddChallenge) =>
        c.id === lastSolveChallengeActivity?.data.challenge_id
    )

    if (lastSolvedChallenge) {
      const currentChallengeIndex =
        compassChallenges.indexOf(lastSolvedChallenge)
      // if there is another challenge we return it
      if (compassChallenges[currentChallengeIndex + 1]) {
        const challenge = compassChallenges[currentChallengeIndex + 1]
        return {
          challenge: challenge as WddMultipleChoiceChallenge,
          index: currentChallengeIndex + 1
        }
      }
      // if there is no other challenge, we return undefined (end of the game)
      return {
        challenge: undefined,
        index: undefined
      }
    } else {
      // if we didn't find the last challenge that was solved, we return the first challenge (start of the game)
      const challenge = compassChallenges[0]
      return {
        challenge: challenge as WddMultipleChoiceChallenge,
        index: 0
      }
    }
  }
  return { challenge: undefined, index: undefined }
}

export const findSelectedGuideAndGroupName = (journeyData: JourneyData) => {
  const activities = journeyData.group.activities
  const sortedActivities = sortActivities(activities)
  const guideActivities = filterActivitiesByKind(
    "select_guide",
    sortedActivities
  )

  const guideAndGroup = guideActivities.shift()
  if (!guideAndGroup) return undefined

  const guideId = guideAndGroup?.data?.guide_id as number
  const groupName = guideAndGroup?.data?.group_name as string

  return { guideId, groupName }
}

export const findCompleteGame = (journeyData: JourneyData) => {
  const activities = journeyData.group.activities
  const sortedActivities = sortActivities(activities)
  const completeGameActivities = filterActivitiesByKind(
    "complete_game",
    sortedActivities
  )
  return completeGameActivities.length > 0
}

export const findCompleteBatchGame = (journeyData: JourneyData) => {
  const activities = journeyData.group.activities
  const sortedActivities = sortActivities(activities)
  const completeGameActivities = filterActivitiesByKind(
    "complete_batch_game",
    sortedActivities
  )
  return completeGameActivities.length > 0
}

export const reconstructHistory = (
  journeyData: JourneyData,
  contentVersion: ContentVersion
): GameHistoryItem[] => {
  const activities = journeyData.group.activities
  const challenges = retrieveOrderedChallengesForPath(
    journeyData.group.path_id,
    contentVersion
  )

  const normalChallenges = challenges?.filter(
    (c: WddChallenge) => !c.name.startsWith("wdd_compass_")
  )

  const sortedActivities = sortActivities(activities)
  const solveChallengeActivities = filterActivitiesByKind(
    "solve_challenge",
    sortedActivities
  )

  if (normalChallenges) {
    const history = solveChallengeActivities
      .map((activity: Activity) => {
        const challenge = normalChallenges.find(
          (c: WddChallenge) => c.id === activity.data.challenge_id
        )
        return {
          challenge: challenge as WddChallenge,
          activity: activity as Activity
        }
      })
      .filter((item) => item.challenge !== undefined)

    return history
  }

  return []
}

export const reconstructCompassHistory = (
  journeyData: JourneyData,
  contentVersion: ContentVersion
): CompassHistoryItem[] => {
  const activities = journeyData.group.activities
  const challenges = retrieveOrderedChallengesForPath(
    journeyData.group.path_id,
    contentVersion
  )

  const compassChallenges = challenges?.filter((c: WddChallenge) =>
    c.name.startsWith("wdd_compass_")
  )

  const sortedActivities = sortActivities(activities)
  const solveCompassChallengeActivities = filterActivitiesByKind(
    "solve_compass_challenge",
    sortedActivities
  )

  if (compassChallenges) {
    const history = solveCompassChallengeActivities
      .map((activity: Activity) => {
        const challenge = compassChallenges.find(
          (c: WddChallenge) => c.id === activity.data.challenge_id
        )
        return {
          challenge: challenge as WddMultipleChoiceChallenge,
          activity: activity as Activity
        }
      })
      .filter((item) => item.challenge !== undefined)

    return history
  }

  return []
}

// Completion status of the route: how many of the city stations (polls
// included, compass questions excluded) have been answered at least once.
// `correct` counts the stations with a right answer among those with one.
// Latest answer per challenge, for city stations (solve_challenge) and
// compass questions (solve_compass_challenge) alike.
export const latestAnswersByChallenge = (
  activities: Activity[]
): Map<number, Activity> => {
  const sorted = sortActivities(activities).filter(
    (a) => a.kind === "solve_challenge" || a.kind === "solve_compass_challenge"
  )
  const latest = new Map<number, Activity>()
  ;[...sorted].reverse().forEach((activity: Activity) => {
    const id = activity.data.challenge_id as number
    if (!latest.has(id)) latest.set(id, activity)
  })
  return latest
}

// All nine challenges of the route count: seven city stations plus the two
// compass questions.
export const calculateProgress = (
  journeyData: JourneyData,
  contentVersion: ContentVersion
): { answered: number; correct: number; total: number } => {
  const stations: WddChallenge[] | undefined = retrieveOrderedChallengesForPath(
    journeyData.group.path_id,
    contentVersion
  )

  if (!stations) return { answered: 0, correct: 0, total: 0 }

  const total = stations.length
  const latestByChallenge = latestAnswersByChallenge(
    journeyData.group.activities
  )

  const answeredStations = stations.filter((c) => latestByChallenge.has(c.id))
  const answered = answeredStations.length
  const correct = answeredStations.filter(
    (c) =>
      !isPollChallenge(c) && latestByChallenge.get(c.id)?.data.correct === true
  ).length

  return { answered, correct, total }
}

export const findLastActivityTimestamp = (journeyData: JourneyData) => {
  const activities = journeyData.group.activities
  const sortedActivities = sortActivities(activities)
  const selectGuideActivities = filterActivitiesByKind(
    "select_guide",
    sortedActivities
  )
  const solveChallengeActivities = filterActivitiesByKind(
    "solve_challenge",
    sortedActivities
  )
  const filteredActivities = [
    ...selectGuideActivities,
    ...solveChallengeActivities
  ]
  const lastActivity = filteredActivities.pop()
  if (!lastActivity?.created_at) return undefined
  return lastActivity?.created_at
}

export const calculateSecondsSinceTimestamp = (
  timestamp: string | undefined
) => {
  if (!timestamp) return 0
  const now = new Date()
  const activityDate = new Date(timestamp)
  if (isNaN(activityDate.getTime())) return 0
  const seconds = Math.floor((now.getTime() - activityDate.getTime()) / 1000)
  if (seconds < 0) return 0
  return seconds
}

export const heuristicallyIdentifyMapFromChallenges = (
  contentVersion: ContentVersion | undefined
): string => {
  if (!contentVersion?.quest?.challenges?.length) return "unknown"

  const prefixes = ["wdd_zurich_", "wdd_uster_", "wdd_winterthur_"]
  const cities = ["zurich", "uster", "winterthur"]

  const wddChallenges = contentVersion.quest!.challenges as WddChallenge[]
  const counts = prefixes.map((prefix: string) => {
    return wddChallenges.filter((c: WddChallenge) => c.name.startsWith(prefix))
      .length
  })

  const maxCount = Math.max(...counts)

  if (maxCount === 0) return "unknown"

  const maxIndex = counts.indexOf(maxCount)

  return cities[maxIndex] || "unknown"
}

export const calculateMapCompletion = (
  journeyData: JourneyData,
  contentVersion: ContentVersion
): boolean[] => {
  const map = heuristicallyIdentifyMapFromChallenges(contentVersion) as
    | "zurich"
    | "uster"
    | "winterthur"
    | "unknown"

  const challengeToMarkerMapping: {
    [key: string]: string[]
  } = {
    zurich: [
      "compass_zurich", // compass always remains "unsolved"
      "wdd_zurich_1",
      "wdd_zurich_2",
      "wdd_zurich_3",
      "wdd_zurich_4",
      "wdd_zurich_5",
      "wdd_zurich_6",
      "wdd_zurich_7"
    ],
    uster: [
      "compass_uster", // compass always remains "unsolved"
      "wdd_uster_1",
      "wdd_uster_2",
      "wdd_uster_3",
      "wdd_uster_4",
      "wdd_uster_5",
      "wdd_uster_6",
      "wdd_uster_7"
    ],
    winterthur: [
      "compass_winterthur", // compass always remains "unsolved"
      "wdd_winterthur_1",
      "wdd_winterthur_2",
      "wdd_winterthur_3",
      "wdd_winterthur_4",
      "wdd_winterthur_5",
      "wdd_winterthur_6",
      "wdd_winterthur_7"
    ]
  }

  const completion: boolean[] = new Array(8).fill(false)

  if (!map || map === "unknown") return completion

  const mapData = challengeToMarkerMapping[map]

  const challenges = retrieveOrderedChallengesForPath(
    journeyData.group.path_id,
    contentVersion
  )

  if (!challenges) return completion

  const sortedActivities = sortActivities(journeyData.group.activities)
  const solveChallengeActivities = filterActivitiesByKind(
    "solve_challenge",
    sortedActivities
  )

  challenges.forEach((challenge: WddChallenge) => {
    const solved = solveChallengeActivities.find(
      (a) => a.data.challenge_id === challenge.id
    )

    if (mapData && solved) {
      const index = mapData.indexOf(challenge.name)
      if (index !== -1) {
        completion[index] = true
      }
    }
  })

  return completion
}

// One entry per answered station for the results lists (scoreboard, success screen).
export type ResultItem = {
  challengeId: number
  kind: "question" | "poll"
  // one of the two closing questions at the Demokratiekompass
  compass: boolean
  question: string
  answer: string
  answerKey: string
  correct: boolean
  pollChallenge?: WddPollChallenge
}

export const buildResultItems = (
  journeyData: JourneyData,
  contentVersion: ContentVersion
): ResultItem[] => {
  const challenges =
    retrieveOrderedChallengesForPath(
      journeyData.group.path_id,
      contentVersion
    ) ?? []
  const recentMap = latestAnswersByChallenge(journeyData.group.activities)

  return challenges
    .filter((challenge) => recentMap.has(challenge.id))
    .map((challenge) => {
      const f = challenge.fragment as Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
      const question = f.question?.de ?? ""
      const activity = recentMap.get(challenge.id)!
      const answerKey = (activity.data.answer as string) ?? ""
      const isCorrect = activity.data.correct === true

      if (isPollChallenge(challenge)) {
        return {
          challengeId: challenge.id,
          kind: "poll" as const,
          compass: false,
          question,
          answer: f[answerKey]?.de ?? "",
          answerKey,
          correct: true,
          pollChallenge: challenge
        }
      }

      let answer = ""
      if (
        f.kind === "wdd_multiple_choice" ||
        f.kind === "wdd_multiple_choice_conditional"
      ) {
        answer = f[answerKey]?.de ?? ""
      } else if (f.kind === "wdd_open_sesame") {
        answer = answerKey
      } else if (f.kind === "wdd_multiple_choice_image") {
        answer = isCorrect
          ? (f.explanation_correct?.de ?? "")
          : (f.explanation_incorrect?.de ?? "")
      }

      return {
        challengeId: challenge.id,
        kind: "question" as const,
        compass: challenge.name.startsWith("wdd_compass_"),
        question,
        answer,
        answerKey,
        correct: isCorrect
      }
    })
}
